import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AnalystProgressStep,
  AnalystProgressStepView,
  AnalystTurnStepSummary,
  AnalystTurnSummaryView,
  ChatMessageResponse,
} from 'src/app/models/chat.model';
import { AnalystOutputBlock, AnalystOutputType, AnalystStopReason, Citation } from 'src/app/models/analyst-output.model';
import { AiReportJobStatus } from 'src/app/enums/aiReportJobStatus.enum';
import { ChatMessageExportFormat } from 'src/app/enums/chatMessageExportFormat.enum';
import { ChatMessageKind } from 'src/app/enums/chatMessageKind.enum';
import { ChatMessageRole } from 'src/app/enums/chatMessageRole.enum';
import { FeedbackRating } from 'src/app/enums/feedbackRating.enum';
import { AskErrorKind } from 'src/app/enums/askErrorKind.enum';
import { ANALYST_PROGRESS_LABELS, AnalystProgressPhase } from 'src/app/enums/analystProgressPhase.enum';
import { ChatService } from 'src/app/services/chat.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { MAX_QUESTION_CHARS } from 'src/app/shared/constants/chat.constants';
import { extractContentDispositionFilename } from 'src/app/shared/utils/content-disposition.util';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { normalizeChatMessageContent } from 'src/app/shared/utils/chat-message-validation.util';
import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { CitationNumbering, numberMessageCitations } from 'src/app/shared/utils/citation-resolver.util';
import { stripAnalystAnswerImages } from '../answer-renderer/strip-analyst-answer-images.util';
import { CollapsedProgressStep, collapseLiveProgressSteps, collapseProgressSteps } from './collapse-progress-steps.util';

const COPIED_RESET_MS = 1500;
const FEEDBACK_THANKS_MS = 1800;
const DEFAULT_PROGRESS_LABEL = 'Analyzing your data…';
const SUB_TENTH_SECOND_LABEL = '<0.1s';

@Component({
  standalone: false,
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatThreadComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() messages: ChatMessageResponse[] = [];
  @Input() isAsking = false;
  @Input() askStopping = false;
  @Input() askCancelled = false;
  @Input() turnError: AskErrorKind | null = null;
  @Input() turnErrorMessage: string | null = null;
  @Input() turnRetryAfterSeconds: number | null = null;
  @Input() progressPhase: AnalystProgressPhase | null = null;
  /** Completed tool calls for the turn currently running, in execution order. Empty on an older BE
   * that doesn't send `steps` yet, or before the first tool call of a turn has completed - either
   * way the template falls back to today's single shimmer line. */
  @Input() progressSteps: AnalystProgressStep[] = [];
  /** Finished turns' step history, keyed by the resulting assistant message id - renders as a
   * collapsed "Worked for Xs · N steps" summary under that message, expandable back to the full list. */
  @Input() turnStepSummaries: Record<string, AnalystTurnStepSummary> = {};
  @Input() reportJobStatus: AiReportJobStatus | null = null;
  @Input() reportJobErrorMessage: string | null = null;
  @Input() reportCancelRequested = false;
  @Input() reportConnectionLost = false;
  @Output() retry = new EventEmitter<void>();
  @Output() optionSelected = new EventEmitter<string>();
  @Output() cancelReport = new EventEmitter<void>();
  @Output() stopAsking = new EventEmitter<void>();
  @Output() regenerate = new EventEmitter<ChatMessageResponse>();
  @Output() editAndResend = new EventEmitter<{ messageId: string; content: string }>();

  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

  @ViewChild('editTextarea') set editTextarea(ref: ElementRef<HTMLTextAreaElement> | undefined) {
    if (ref) {
      this.autoGrowEdit(ref.nativeElement);
    }
  }

  readonly trackById = trackById;
  readonly trackByIndex = trackByIndex;
  readonly ChatMessageRole = ChatMessageRole;
  readonly ChatMessageKind = ChatMessageKind;
  readonly feedbackRating = FeedbackRating;
  readonly AiReportJobStatus = AiReportJobStatus;
  readonly ChatMessageExportFormat = ChatMessageExportFormat;
  readonly maxLength = MAX_QUESTION_CHARS;

  /** Falls back to the default shimmer label when there's no known phase (flag off, cache miss, or between polls). */
  progressLabel = DEFAULT_PROGRESS_LABEL;

  /** `progressSteps` translated into completed rows for the running timeline. A run the current phase
   * continues is left out and counted in `progressCurrentCount` on the spinner row instead. */
  progressStepViews: AnalystProgressStepView[] = [];
  progressCurrentCount = 1;

  /** `turnStepSummaries` translated into rows for the collapsed per-message summary. */
  turnSummaryViewByMessageId: Record<string, AnalystTurnSummaryView> = {};

  /** Which finished-turn summaries are expanded back to their full step list. Collapsed by default. */
  expandedSummaryMessageIds: Record<string, boolean> = {};

  copiedMessageId: string | null = null;
  feedbackByMessageId: Record<string, FeedbackRating> = {};
  feedbackPopupMessageId: string | null = null;
  feedbackReasonInput = '';
  feedbackMinutesSavedInput: number | null = null;
  feedbackThanksMessageId: string | null = null;

  /** The last assistant/user message ids in `messages`, recomputed on every `messages` change. Regenerate and
   * edit are only offered on these, since anything earlier would silently discard later turns on a rewind. */
  lastAssistantMessageId: string | null = null;
  lastUserMessageId: string | null = null;

  /** Per-message clarification state, recomputed on every `messages` change so the template binds a field
   * instead of a method call. A clarification counts as answered once any later message exists. */
  clarificationAnsweredByIndex: boolean[] = [];
  clarificationAnswerTextByIndex: (string | null)[] = [];

  /** Per-message caveat translation key, recomputed on every `messages` change so the template binds a
   * field instead of a method call. Null when the turn completed cleanly with no known gaps. A turn that
   * both ran out of time AND is missing data gets a single combined key rather than stacking two boxes. */
  turnCaveatKeyByIndex: (string | null)[] = [];

  citationNumberingByIndex: CitationNumbering[] = [];

  /** True while a turn is in flight, whether an ask or a report generation. Regenerate and edit-and-resend
   * are disabled the whole time, since either would rewind a chat that the server is still writing to. */
  isTurnInFlight = false;

  editingMessageId: string | null = null;
  editInput = '';
  editHasContent = false;
  editCharCount = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly feedbackSyncedMessageIds = new Set<string>();
  private readonly citationNumberingByCitations = new WeakMap<Citation[], { content: string; outputs: AnalystOutputBlock[]; numbering: CitationNumbering }>();
  private pendingScroll = false;
  private pendingFeedbackPopupScroll = false;
  private copiedResetTimer?: ReturnType<typeof setTimeout>;
  private feedbackThanksTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly chatService: ChatService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messages']) {
      this.syncFeedbackFromMessages();
      this.recomputeLastMessageIds();
      this.recomputeClarificationAnswers();
      this.recomputeTurnCaveats();
      this.recomputeCitationNumbering();
    }
    if (changes['messages'] || changes['isAsking'] || changes['askStopping'] || changes['askCancelled'] || changes['turnError'] || changes['reportJobStatus']) {
      this.pendingScroll = true;
    }
    if (changes['isAsking'] || changes['reportJobStatus']) {
      this.recomputeIsTurnInFlight();
    }
    if (changes['progressPhase']) {
      this.recomputeProgressLabel();
    }
    if (changes['progressPhase'] || changes['progressSteps']) {
      this.recomputeProgressStepViews();
    }
    if (changes['turnStepSummaries']) {
      this.recomputeTurnSummaryViews();
    }
  }

  toggleTurnSummary(messageId: string): void {
    this.expandedSummaryMessageIds = { ...this.expandedSummaryMessageIds, [messageId]: !this.expandedSummaryMessageIds[messageId] };
  }

  ngAfterViewChecked(): void {
    if (this.pendingScroll) {
      this.pendingScroll = false;
      this.scrollToBottom();
    }
    if (this.pendingFeedbackPopupScroll) {
      this.pendingFeedbackPopupScroll = false;
      this.scrollFeedbackPopupIntoView();
    }
  }

  ngOnDestroy(): void {
    if (this.copiedResetTimer) {
      clearTimeout(this.copiedResetTimer);
    }
    if (this.feedbackThanksTimer) {
      clearTimeout(this.feedbackThanksTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyAnswer(message: ChatMessageResponse): void {
    navigator.clipboard
      .writeText(message.content)
      .then(() => {
        this.copiedMessageId = message.id;
        this.cdr.markForCheck();
        if (this.copiedResetTimer) {
          clearTimeout(this.copiedResetTimer);
        }
        this.copiedResetTimer = setTimeout(() => {
          this.copiedMessageId = null;
          this.cdr.markForCheck();
        }, COPIED_RESET_MS);
      })
      .catch(() => {
        this.toastService.displayErrorMessage('Failed to copy', true);
      });
  }

  onThumbsUp(message: ChatMessageResponse): void {
    if (this.feedbackByMessageId[message.id] === FeedbackRating.Up) {
      this.clearFeedback(message.id);
      return;
    }
    this.submitFeedback(message.id, FeedbackRating.Up);
  }

  onThumbsDown(message: ChatMessageResponse): void {
    if (this.feedbackByMessageId[message.id] === FeedbackRating.Down) {
      this.clearFeedback(message.id);
      return;
    }
    this.feedbackPopupMessageId = message.id;
    this.feedbackReasonInput = '';
    this.feedbackMinutesSavedInput = null;
    this.pendingFeedbackPopupScroll = true;
  }

  submitFeedbackReason(message: ChatMessageResponse): void {
    this.submitFeedback(
      message.id,
      FeedbackRating.Down,
      this.feedbackReasonInput.trim() || undefined,
      this.feedbackMinutesSavedInput == null ? undefined : Math.round(this.feedbackMinutesSavedInput));
  }

  cancelFeedbackPopup(): void {
    this.feedbackPopupMessageId = null;
    this.feedbackReasonInput = '';
    this.feedbackMinutesSavedInput = null;
  }

  onRetry(): void {
    this.retry.emit();
  }

  onCancelReportClick(): void {
    this.cancelReport.emit();
  }

  /** Requests a real cancel of the in-flight turn. The parent stays in the stopping state until the
   * server confirms the cancel, since the turn keeps running for a few seconds after this click. */
  onStopAskClick(): void {
    this.stopAsking.emit();
  }

  onRegenerateClick(message: ChatMessageResponse): void {
    this.regenerate.emit(message);
  }

  startEdit(message: ChatMessageResponse): void {
    this.editingMessageId = message.id;
    this.editInput = message.content;
    this.editHasContent = message.content.trim().length > 0;
    this.editCharCount = message.content.length;
  }

  onEditInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    this.editInput = value;
    this.editHasContent = value.trim().length > 0;
    this.editCharCount = value.length;
    this.autoGrowEdit(target);
  }

  private autoGrowEdit(element: HTMLTextAreaElement): void {
    element.style.height = 'auto';
    const borders = element.offsetHeight - element.clientHeight;
    element.style.height = `${element.scrollHeight + borders}px`;
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editInput = '';
    this.editHasContent = false;
    this.editCharCount = 0;
  }

  saveEdit(messageId: string): void {
    const content = normalizeChatMessageContent(this.editInput, this.maxLength);
    if (content === null) {
      return;
    }
    this.cancelEdit();
    this.editAndResend.emit({ messageId, content });
  }

  onExportClick(message: ChatMessageResponse, format: ChatMessageExportFormat): void {
    this.chatService
      .exportMessage(message.id, format)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.saveExportResponse(response, message.id, format),
        error: () => this.toastService.displayErrorMessage('Export failed', true),
      });
  }

  private saveExportResponse(response: HttpResponse<Blob>, messageId: string, format: ChatMessageExportFormat): void {
    const blob = response.body;
    if (!blob) {
      this.toastService.displayErrorMessage('Export failed', true);
      return;
    }

    const filename = extractContentDispositionFilename(response.headers.get('Content-Disposition')) ?? this.buildFallbackExportFilename(messageId, format);
    downloadBlob(blob, filename);
  }

  private buildFallbackExportFilename(messageId: string, format: ChatMessageExportFormat): string {
    const extension = format === ChatMessageExportFormat.Pdf ? 'pdf' : 'xlsx';
    return `report-${messageId}.${extension}`;
  }

  private recomputeClarificationAnswers(): void {
    const lastIndex = this.messages.length - 1;
    this.clarificationAnsweredByIndex = this.messages.map((_, index) => index < lastIndex);
    this.clarificationAnswerTextByIndex = this.messages.map((_, index) => {
      const next = this.messages[index + 1];
      return next?.role === ChatMessageRole.User ? next.content : null;
    });
  }

  private recomputeTurnCaveats(): void {
    this.turnCaveatKeyByIndex = this.messages.map((message) => this.buildTurnCaveatKey(message));
  }

  private recomputeCitationNumbering(): void {
    this.citationNumberingByIndex = this.messages.map(({ content, outputs, citations }) => {
      const cached = this.citationNumberingByCitations.get(citations);
      if (cached?.content === content && cached.outputs === outputs) {
        return cached.numbering;
      }
      const numbering = numberMessageCitations(stripAnalystAnswerImages(content), outputs, citations);
      this.citationNumberingByCitations.set(citations, { content, outputs, numbering });
      return numbering;
    });
  }

  private buildTurnCaveatKey(message: ChatMessageResponse): string | null {
    const isSlow = message.stopReason === AnalystStopReason.WallClockExceeded || message.stopReason === AnalystStopReason.ModelCallTimedOut;
    const hasCompletedOffer = message.outputs.some(
      (output) => output.type === AnalystOutputType.Clarification || output.type === AnalystOutputType.NarrowingOptions,
    );
    const showsCutOffCaveat = message.stopReason === AnalystStopReason.OutputTruncated && !hasCompletedOffer;
    if (isSlow && message.hasIncompleteData) {
      return 'This answer may be incomplete because it took too long to generate and some of the requested data could not be retrieved.';
    }
    if (isSlow) {
      return 'This answer may be incomplete because it took too long to generate.';
    }
    if (showsCutOffCaveat && message.hasIncompleteData) {
      return 'This answer may be incomplete because it was cut off partway through and some of the requested data could not be retrieved.';
    }
    if (showsCutOffCaveat) {
      return 'This answer may be incomplete because it was cut off partway through.';
    }
    if (message.hasIncompleteData) {
      return 'This answer may be incomplete because some of the requested data could not be retrieved.';
    }
    return null;
  }

  private recomputeLastMessageIds(): void {
    this.lastAssistantMessageId = null;
    this.lastUserMessageId = null;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i];
      if (this.lastAssistantMessageId === null && message.role === ChatMessageRole.Assistant) {
        this.lastAssistantMessageId = message.id;
      }
      if (this.lastUserMessageId === null && message.role === ChatMessageRole.User) {
        this.lastUserMessageId = message.id;
      }
      if (this.lastAssistantMessageId !== null && this.lastUserMessageId !== null) {
        break;
      }
    }
  }

  private submitFeedback(messageId: string, rating: FeedbackRating, reason?: string, estimatedMinutesSaved?: number): void {
    this.chatService
      .submitFeedback({ messageId, rating, reason, estimatedMinutesSaved })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.feedbackByMessageId = { ...this.feedbackByMessageId, [messageId]: rating };
          this.feedbackPopupMessageId = null;
          this.feedbackThanksMessageId = messageId;
          this.cdr.markForCheck();
          if (this.feedbackThanksTimer) {
            clearTimeout(this.feedbackThanksTimer);
          }
          this.feedbackThanksTimer = setTimeout(() => {
            this.feedbackThanksMessageId = null;
            this.cdr.markForCheck();
          }, FEEDBACK_THANKS_MS);
        },
        error: () => {
          this.toastService.displayErrorMessage('Failed to submit feedback', true);
        },
      });
  }

  private clearFeedback(messageId: string): void {
    this.chatService
      .clearFeedback(messageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const updatedFeedbackByMessageId = { ...this.feedbackByMessageId };
          delete updatedFeedbackByMessageId[messageId];
          this.feedbackByMessageId = updatedFeedbackByMessageId;
          this.feedbackPopupMessageId = null;
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastService.displayErrorMessage('Failed to update feedback', true);
        },
      });
  }

  private syncFeedbackFromMessages(): void {
    const updatedFeedbackByMessageId = { ...this.feedbackByMessageId };
    let hasUpdate = false;

    for (const message of this.messages) {
      if (this.feedbackSyncedMessageIds.has(message.id)) {
        continue;
      }
      this.feedbackSyncedMessageIds.add(message.id);
      if (message.rating !== null) {
        updatedFeedbackByMessageId[message.id] = message.rating;
        hasUpdate = true;
      }
    }

    if (hasUpdate) {
      this.feedbackByMessageId = updatedFeedbackByMessageId;
    }
  }

  private scrollToBottom(): void {
    const element = this.scrollContainer?.nativeElement;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }

  private scrollFeedbackPopupIntoView(): void {
    const popup = this.scrollContainer?.nativeElement.querySelector<HTMLElement>('.feedback-popup');
    popup?.scrollIntoView({ block: 'nearest' });
  }

  private recomputeProgressLabel(): void {
    this.progressLabel = ANALYST_PROGRESS_LABELS[this.progressPhase] ?? DEFAULT_PROGRESS_LABEL;
  }

  private recomputeProgressStepViews(): void {
    const { completed, currentCount } = collapseLiveProgressSteps(this.progressSteps, this.progressPhase);
    this.progressStepViews = completed.map((step) => this.buildStepView(step));
    this.progressCurrentCount = currentCount;
  }

  private recomputeTurnSummaryViews(): void {
    const viewsByMessageId: Record<string, AnalystTurnSummaryView> = {};
    for (const [messageId, summary] of Object.entries(this.turnStepSummaries)) {
      viewsByMessageId[messageId] = {
        // A fast (or resumed, poll-just-started) turn can round to 0 - "Worked for 0s" reads as
        // broken, not quick, so the floor is 1 whole second even when the real elapsed time is less.
        totalSeconds: Math.max(1, Math.round(summary.totalMs / 1000)),
        stepCount: summary.steps.length,
        steps: collapseProgressSteps(summary.steps).map((step) => this.buildStepView(step)),
      };
    }
    this.turnSummaryViewByMessageId = viewsByMessageId;
  }

  private buildStepView(step: CollapsedProgressStep): AnalystProgressStepView {
    return {
      labelKey: ANALYST_PROGRESS_LABELS[step.phase] ?? DEFAULT_PROGRESS_LABEL,
      durationText: this.formatStepDuration(step.durationMs),
      count: step.count,
    };
  }

  /** `toFixed(1)` alone renders any sub-50ms step as `0.0s`, which reads as "this didn't run" -
   * exactly what the timeline exists to disprove. Checking the formatted string (rather than
   * duplicating the rounding threshold as a separate constant) catches every value that would
   * print as zero, however it got there. */
  private formatStepDuration(durationMs: number): string {
    const formatted = (durationMs / 1000).toFixed(1);
    return formatted === '0.0' ? this.translateService.getDynamicTranslation(SUB_TENTH_SECOND_LABEL) : `${formatted}s`;
  }

  private recomputeIsTurnInFlight(): void {
    this.isTurnInFlight = this.isAsking || this.reportJobStatus === AiReportJobStatus.Queued || this.reportJobStatus === AiReportJobStatus.Running;
  }
}
